import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  AiOutlineClose,
  AiOutlineCamera,
  AiOutlineDelete,
  AiFillThunderbolt,
  AiOutlineThunderbolt,
  AiOutlineCheckCircle,
  AiOutlineCloseCircle,
  AiOutlineReload,
  AiOutlineVideoCamera,
} from 'react-icons/ai';
import { apiUrl } from '../config/api';
import * as uploads from '../lib/uploadQueue';
import { readVideo, formatDuration, isVideoFile } from '../lib/videoPoster';
import { VIDEO_MAX_BYTES } from '../lib/videoUpload';
import { useAppSettings } from '../lib/appSettings';
import './CameraCaptureModal.css';

// Ceiling for drag zoom when the camera has no real zoom and we're cropping pixels
// instead. Past this a 1280-wide feed has too little left to be worth keeping.
const MAX_DIGITAL_ZOOM = 3;
// Zoom is two-finger pinch only. A one-finger drag is deliberately NOT a zoom
// gesture here: with no on-screen buttons, a thumb resting on the preview while
// gripping the phone would silently re-frame the shot, and the technician would
// only find out after the photo was taken.
const MAX_DIMENSION = 1920;
const JPEG_QUALITY = 0.85;

// ── Video recording ────────────────────────────────────────────────────────
// A recording is held in the phone's memory until it stops, so cap its length.
// At 2.5 Mbps (720p) ten minutes is ~190 MB — fine to hold, quick-ish to send.
const MAX_RECORD_SECONDS = 10 * 60;
const VIDEO_BITRATE = 2500000;
// MP4 first: it plays everywhere, including iPhones. Chrome on Android also
// records MP4 now; older Chromes fall back to WebM.
const RECORD_TYPES = [
  'video/mp4;codecs=avc1,mp4a',
  'video/mp4',
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
];
const recorderSupported = typeof window !== 'undefined' && typeof window.MediaRecorder !== 'undefined';
const pickRecordType = () =>
  RECORD_TYPES.find((t) => window.MediaRecorder.isTypeSupported?.(t)) || '';

// ── Image helpers ──────────────────────────────────────────────────────────

function resizeImageFile(file) {
  return new Promise((resolve) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const scale = Math.min(1, MAX_DIMENSION / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      canvas.toBlob(
        (blob) => resolve(blob ? new File([blob], file.name, { type: 'image/jpeg' }) : file),
        'image/jpeg', JPEG_QUALITY
      );
    };
    img.onerror = () => { URL.revokeObjectURL(objectUrl); resolve(file); };
    img.src = objectUrl;
  });
}

// ── Per-image upload status ────────────────────────────────────────────────
// Each photo in capturedPhotos has: { id, file, preview, status }
// status: 'pending' (queued) | 'uploading' | 'done' | 'failed'
// Photos upload as soon as they're captured/picked — see enqueueUpload.

function makePhoto(file, preview) {
  return { id: `${Date.now()}-${Math.random().toString(36).slice(2)}`, file, preview, status: 'pending' };
}

async function makeVideo(file, knownDurationMs = null) {
  const { posterBlob, posterDataUrl, durationMs } = await readVideo(file);
  return {
    ...makePhoto(file, posterDataUrl),
    kind: 'video',
    posterBlob,
    // Browser recordings (WebM especially) often report no length; the timer knows it.
    durationMs: durationMs || knownDurationMs,
  };
}

// ── Main component ─────────────────────────────────────────────────────────

const CameraCaptureModal = ({ isOpen, onClose, clients, user, preselectedClient }) => {
  const [mode, setMode] = useState('select'); // 'confirm' | 'select' | 'capture'
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientName, setClientName] = useState('');
  const [clientAddress, setClientAddress] = useState('');
  const [clientSuggestions, setClientSuggestions] = useState([]);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [inspectionDate, setInspectionDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [cameraStream, setCameraStream] = useState(null);


  // Uploads are owned by lib/uploadQueue, outside React, so closing this modal no
  // longer cancels them. Each visit to the camera is one "batch".
  const batchIdRef = useRef(null);
  const [queueItems, setQueueItems] = useState([]);

  const [error, setError] = useState('');
  const [zoomLevel, setZoomLevel] = useState(1);
  const [zoomCapabilities, setZoomCapabilities] = useState(null);
  const [torchSupported, setTorchSupported] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  // Video is switched on/off by the main admin; re-read each time the camera opens.
  const { videoEnabled } = useAppSettings({ refresh: isOpen });
  const canRecordVideo = videoEnabled && recorderSupported;
  const [chosenKind, setCaptureKind] = useState('photo'); // 'photo' | 'video'
  // Falls back to photos at once if video is switched off mid-session.
  const captureKind = canRecordVideo ? chosenKind : 'photo';
  const [recording, setRecording] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const [noMicNotice, setNoMicNotice] = useState(false);
  const recorderRef = useRef(null);
  const audioStreamRef = useRef(null);
  const recordTimerRef = useRef(null);
  const recordStopRef = useRef(null); // resolves once the clip is queued
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Live drag/pinch zoom on the preview. Pointer state lives in refs because it
  // changes on every pointermove, and re-rendering at that rate makes the gesture
  // feel sticky. `pendingZoom`/`rafRef` throttle the actual applyConstraints call
  // to one per animation frame - the camera cannot keep up with every move event.
  const pointersRef = useRef(new Map());
  const gestureRef = useRef(null);
  const pendingZoomRef = useRef(null);
  const rafRef = useRef(0);
  const [zoomDragging, setZoomDragging] = useState(false);
  const fileInputRef = useRef(null);
  const token = localStorage.getItem('token');
  const today = new Date().toISOString().split('T')[0];

  // ── Client / address suggestion handlers ──────────────────────────────────
  // Suggestions come from a server search (name or address) so every project is reachable —
  // including backup projects — not just the first page of clients preloaded into `clients`.
  // The preloaded list fills in instantly while the search is in flight.
  const searchTimerRef = useRef(null);
  const searchSeqRef = useRef(0);

  const searchClients = (value, setSuggestions, localMatch) => {
    const term = value.trim();
    clearTimeout(searchTimerRef.current);
    if (!term) { setSuggestions([]); return; }
    setSuggestions(clients.filter(localMatch).slice(0, 8));
    const seq = ++searchSeqRef.current;
    searchTimerRef.current = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ search: term, limit: '8' });
        const response = await fetch(apiUrl(`/api/scraper/clients?${params.toString()}`), {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        // Drop responses for a term the user has already typed past.
        if (response.ok && seq === searchSeqRef.current) setSuggestions(data.clients || []);
      } catch (err) {
        // Keep the local suggestions if the search fails (e.g. flaky signal on-site).
      }
    }, 250);
  };

  useEffect(() => () => clearTimeout(searchTimerRef.current), []);

  const handleClientNameChange = (value) => {
    setClientName(value);
    const q = value.trim().toLowerCase();
    searchClients(value, setClientSuggestions, (c) =>
      c.clientName.toLowerCase().includes(q) || (c.clientAddress && c.clientAddress.toLowerCase().includes(q))
    );
    setShowSuggestions(!!q);
    setSelectedClient(null);
  };

  const handleSelectClient = (client) => {
    setClientName(client.clientName);
    setClientAddress(client.clientAddress || '');
    setSelectedClient(client);
    setShowSuggestions(false);
  };

  const handleClientAddressChange = (value) => {
    setClientAddress(value);
    const q = value.trim().toLowerCase();
    searchClients(value, setAddressSuggestions, (c) => c.clientAddress && c.clientAddress.toLowerCase().includes(q));
    setShowAddressSuggestions(!!q);
  };

  const handleSelectAddress = (client) => {
    setClientAddress(client.clientAddress);
    setClientName(client.clientName);
    setSelectedClient(client);
    setShowAddressSuggestions(false);
  };

  // ── Camera ────────────────────────────────────────────────────────────────
  const handleStartCamera = async () => {
    if (!selectedClient && !clientName) { setError('Please select or enter a client name'); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      const track = stream.getVideoTracks()[0];
      const cap = track?.getCapabilities ? track.getCapabilities() : {};
      setZoomCapabilities(cap.zoom || null);
      setTorchSupported(!!cap.torch);
      setTorchOn(false);
      setZoomLevel(1);
      setCameraStream(stream);
      setMode('capture');
      setError('');
    } catch (err) {
      setError(`Camera access failed: ${err.message}`);
    }
  };

  /** Bounds for the current camera: its real range, or the digital-crop ceiling. */
  const zoomRange = () => ({
    min: zoomCapabilities?.min ?? 1,
    max: zoomCapabilities?.max ?? MAX_DIGITAL_ZOOM,
  });

  const handleSetZoom = async (level) => {
    setZoomLevel(level);
    const track = cameraStream?.getVideoTracks()[0];
    if (track && zoomCapabilities) {
      const clamped = Math.min(Math.max(level, zoomCapabilities.min), zoomCapabilities.max);
      try { await track.applyConstraints({ advanced: [{ zoom: clamped }] }); } catch {}
    }
  };

  // ── Drag / pinch zoom on the live preview ────────────────────────────────
  // Applied at most once per frame; the newest value always wins.
  const flushZoom = useCallback(() => {
    rafRef.current = 0;
    const next = pendingZoomRef.current;
    if (next == null) return;
    pendingZoomRef.current = null;
    handleSetZoom(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraStream, zoomCapabilities]);

  const queueZoom = useCallback((value) => {
    const { min, max } = zoomRange();
    pendingZoomRef.current = Math.min(max, Math.max(min, value));
    setZoomLevel(pendingZoomRef.current); // keep the readout immediate
    if (!rafRef.current) rafRef.current = requestAnimationFrame(flushZoom);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flushZoom, zoomCapabilities]);

  const handleZoomPointerDown = (e) => {
    if (!cameraStream) return;
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    e.currentTarget.setPointerCapture?.(e.pointerId);

    const pts = [...pointersRef.current.values()];
    if (pts.length === 2) {
      gestureRef.current = {
        kind: 'pinch',
        startDist: Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) || 1,
        startZoom: zoomLevel,
      };
    }
    // One finger: nothing. Zoom needs two.
    setZoomDragging(pointersRef.current.size >= 2);
  };

  const handleZoomPointerMove = (e) => {
    if (!pointersRef.current.has(e.pointerId)) return;
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gestureRef.current;
    if (!g) return;

    if (g.kind !== 'pinch') return;
    const pts = [...pointersRef.current.values()];
    if (pts.length < 2) return;
    const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) || 1;
    queueZoom(g.startZoom * (dist / g.startDist));
  };

  const handleZoomPointerUp = (e) => {
    pointersRef.current.delete(e.pointerId);
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    // A pinch ends the moment it stops being two fingers.
    if (pointersRef.current.size < 2) {
      gestureRef.current = null;
      setZoomDragging(false);
    }
  };

  const handleToggleTorch = async () => {
    const track = cameraStream?.getVideoTracks()[0];
    if (!track || !torchSupported) return;
    const next = !torchOn;
    try { await track.applyConstraints({ advanced: [{ torch: next }] }); setTorchOn(next); }
    catch { setError('Unable to toggle flash on this device'); }
  };

  const handleCapturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const useDigitalCrop = !zoomCapabilities && zoomLevel > 1;
    const srcW = useDigitalCrop ? video.videoWidth / zoomLevel : video.videoWidth;
    const srcH = useDigitalCrop ? video.videoHeight / zoomLevel : video.videoHeight;
    const srcX = useDigitalCrop ? (video.videoWidth - srcW) / 2 : 0;
    const srcY = useDigitalCrop ? (video.videoHeight - srcH) / 2 : 0;
    const scale = Math.min(1, MAX_DIMENSION / Math.max(srcW, srcH));
    const w = Math.round(srcW * scale), h = Math.round(srcH * scale);
    const ctx = canvasRef.current.getContext('2d');
    canvasRef.current.width = w; canvasRef.current.height = h;
    ctx.drawImage(video, srcX, srcY, srcW, srcH, 0, 0, w, h);
    canvasRef.current.toBlob((blob) => {
      const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
      addAndUpload(makePhoto(file, canvasRef.current.toDataURL('image/jpeg', JPEG_QUALITY)));
    }, 'image/jpeg', JPEG_QUALITY);
  };

  // ── Video recording ──────────────────────────────────────────────────────
  // The microphone is only asked for when switching to Video, not for photos.
  const handleSelectKind = async (kind) => {
    if (recording || kind === captureKind) return;
    setCaptureKind(kind);
    if (kind === 'video' && !audioStreamRef.current) {
      try {
        audioStreamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
        setNoMicNotice(false);
      } catch {
        setNoMicNotice(true); // still records, just silent
      }
    }
  };

  const handleStartRecording = () => {
    if (!cameraStream || recording) return;
    const tracks = [
      ...cameraStream.getVideoTracks(),
      ...(audioStreamRef.current ? audioStreamRef.current.getAudioTracks() : []),
    ];
    const mimeType = pickRecordType();
    let recorder;
    try {
      recorder = new MediaRecorder(new MediaStream(tracks), {
        ...(mimeType ? { mimeType } : {}),
        videoBitsPerSecond: VIDEO_BITRATE,
      });
    } catch (err) {
      setError(`Video recording isn't supported here: ${err.message}`);
      return;
    }
    const chunks = [];
    const startedAt = Date.now();
    recorder.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
    recorder.onstop = async () => {
      clearInterval(recordTimerRef.current);
      setRecording(false);
      setRecordSeconds(0);
      recorderRef.current = null;
      const type = (recorder.mimeType || mimeType || 'video/mp4').split(';')[0];
      const ext = type === 'video/webm' ? 'webm' : 'mp4';
      const blob = new Blob(chunks, { type });
      if (blob.size) {
        const file = new File([blob], `video-${Date.now()}.${ext}`, { type });
        addAndUpload(await makeVideo(file, Date.now() - startedAt));
      }
      recordStopRef.current?.();
      recordStopRef.current = null;
    };
    recorder.start(1000); // hand over data every second, so a crash keeps most of it
    recorderRef.current = recorder;
    setRecording(true);
    setRecordSeconds(0);
    recordTimerRef.current = setInterval(() => {
      const secs = Math.floor((Date.now() - startedAt) / 1000);
      setRecordSeconds(secs);
      if (secs >= MAX_RECORD_SECONDS) recorderRef.current?.stop();
    }, 500);
  };

  /** Stops the recording; resolves once the clip is in the upload queue. */
  const handleStopRecording = () => new Promise((resolve) => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === 'inactive') { resolve(); return; }
    recordStopRef.current = resolve;
    recorder.stop();
  });

  // Only failed photos can be discarded — anything else is already (being) uploaded.
  const handleRemovePhoto = (id) => uploads.discard(id);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (fileInputRef.current) fileInputRef.current.value = '';
    for (const original of files) {
      if (isVideoFile(original)) {
        if (!videoEnabled) {
          setError('Video uploads are coming soon — only photos can be uploaded for now.');
          continue;
        }
        if (original.size > VIDEO_MAX_BYTES) {
          setError(`${original.name} is over 2 GB and can't be uploaded.`);
          continue;
        }
        addAndUpload(await makeVideo(original));
        continue;
      }
      const file = await resizeImageFile(original);
      const preview = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve(ev.target.result);
        reader.readAsDataURL(file);
      });
      addAndUpload(makePhoto(file, preview));
    }
  };

  // ── Upload — each photo is sent the moment it's captured/picked ──────────
  const stopCamera = useCallback(() => {
    // Drop any in-flight zoom gesture with the stream it belonged to, so a queued
    // frame can't fire applyConstraints against a track that has just been stopped.
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = 0; }
    pendingZoomRef.current = null;
    pointersRef.current.clear();
    gestureRef.current = null;
    setZoomDragging(false);

    if (recorderRef.current && recorderRef.current.state !== 'inactive') recorderRef.current.stop();
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((t) => t.stop());
      audioStreamRef.current = null;
    }
    setCaptureKind('photo');
    setNoMicNotice(false);

    if (cameraStream) {
      cameraStream.getTracks().forEach((t) => t.stop());
      setCameraStream(null);
      setZoomLevel(1); setZoomCapabilities(null); setTorchSupported(false); setTorchOn(false);
    }
  }, [cameraStream]);

  useEffect(() => uploads.subscribe(setQueueItems), []);

  // Anything unfinished should survive a stray tab close, so warn — far gentler
  // than the old behaviour of refusing to let the modal close at all.
  useEffect(() => {
    const warn = (e) => {
      if (!uploads.hasPendingUploads()) return undefined;
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, []);

  // One batch per camera session, created lazily on the first photo.
  const ensureBatch = () => {
    if (!batchIdRef.current) {
      batchIdRef.current = `b${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
    uploads.startBatch(batchIdRef.current, {
      clientId: selectedClient?.id || '',
      clientName,
      clientAddress: clientAddress || '',
      inspectionDate,
      token,
    });
    return batchIdRef.current;
  };

  const addAndUpload = (photo) => {
    const batchId = ensureBatch();
    uploads.addPhoto({
      batchId,
      id: photo.id,
      file: photo.file,
      preview: photo.preview,
      kind: photo.kind || 'photo',
      posterBlob: photo.posterBlob || null,
      durationMs: photo.durationMs || null,
    });
  };

  const handleRetryFailed = () => {
    setError('');
    if (batchIdRef.current) uploads.retryFailed(batchIdRef.current);
  };

  // This batch's photos, mirrored out of the queue.
  const capturedPhotos = batchIdRef.current
    ? queueItems.filter((i) => i.batchId === batchIdRef.current)
    : [];

  const counts = capturedPhotos.reduce(
    (acc, p) => { acc[p.status] = (acc[p.status] || 0) + 1; return acc; },
    { pending: 0, uploading: 0, done: 0, failed: 0 }
  );
  const inFlight = counts.pending + counts.uploading;
  const uploading = inFlight > 0;
  const hasVideo = capturedPhotos.some((p) => p.kind === 'video');
  const hasPhoto = capturedPhotos.some((p) => p.kind !== 'video');
  const noun = (n) => {
    const base = hasVideo && hasPhoto ? 'file' : hasVideo ? 'video' : 'photo';
    return `${n} ${base}${n !== 1 ? 's' : ''}`;
  };

  // ── Lifecycle ─────────────────────────────────────────────────────────────
  const resetModal = () => {
    setMode('select');
    setSelectedClient(null); setClientName(''); setClientAddress('');
    setInspectionDate(new Date().toISOString().split('T')[0]);
    // Finished photos are dropped from the queue; anything still going keeps going.
    if (batchIdRef.current) uploads.clearCompleted(batchIdRef.current);
    batchIdRef.current = null;
    setError('');
    stopCamera();
  };

  const handleClose = async () => {
    // A recording in progress is kept: stop it and queue the clip before closing.
    if (recording) await handleStopRecording();
    // Closing no longer waits for uploads — they continue in the background, which
    // is the whole point. Only a *failed* photo is at risk of being lost, so that
    // is the only case worth stopping for.
    if (counts.failed > 0 &&
        !window.confirm(`${noun(counts.failed)} failed to upload. Close and lose them?`)) return;
    resetModal();
    onClose();
  };

  useEffect(() => {
    if (isOpen && preselectedClient) {
      setSelectedClient(preselectedClient);
      setClientName(preselectedClient.clientName);
      setClientAddress(preselectedClient.clientAddress || '');
      setInspectionDate(today);
      setMode('confirm');
    } else if (isOpen && !preselectedClient) {
      setMode('select');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, preselectedClient]);

  useEffect(() => {
    if (mode === 'capture' && cameraStream && videoRef.current) {
      videoRef.current.srcObject = cameraStream;
      videoRef.current.play().catch(() => {});
    }
  }, [mode, cameraStream]);

  useEffect(() => {
    return () => { if (cameraStream) cameraStream.getTracks().forEach((t) => t.stop()); };
  }, [cameraStream]);

  if (!isOpen) return null;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="ccm-overlay">
        <div className="ccm-modal">
          <div className="ccm-header">
            <h2 className="ccm-title">{videoEnabled ? 'Photos & Videos' : 'Capture & Upload Photos'}</h2>
            <button
              className="ccm-close"
              onClick={handleClose}
              aria-label="Close"

              style={uploading ? { opacity: 0.35, cursor: 'not-allowed' } : undefined}
            >
              <AiOutlineClose size={20} />
            </button>
          </div>

          <div className="ccm-body">
            {error && <div className="ccm-message ccm-message--error">{error}</div>}

            {/* ── Confirm screen ── */}
            {mode === 'confirm' && (
              <div className="ccm-section">
                <div className="ccm-confirm-card">
                  <p className="ccm-confirm-label">Capturing photos for</p>
                  <p className="ccm-confirm-name">
                    {clientName}
                    {selectedClient?.storageStatus === 'legacy' && <span className="ccm-backup-badge">Backup</span>}
                  </p>
                  {clientAddress && <p className="ccm-confirm-address">{clientAddress}</p>}
                </div>
                <div className="ccm-form-group">
                  <label className="ccm-label">Inspection Date *</label>
                  <input type="date" className="ccm-input" value={inspectionDate}
                    onChange={(e) => setInspectionDate(e.target.value)} max={today} required />
                </div>
                <div className="ccm-button-group">
                  <button className="ccm-btn ccm-btn--primary" onClick={handleStartCamera}>
                    <AiOutlineCamera size={16} />Start Capturing
                  </button>
                  <button className="ccm-btn ccm-btn--secondary"
                    onClick={() => { setSelectedClient(null); setClientName(''); setClientAddress(''); setMode('select'); }}>
                    Not This Client
                  </button>
                </div>
              </div>
            )}

            {/* ── Select screen ── */}
            {mode === 'select' && (
              <div className="ccm-section">
                <div className="ccm-form-group">
                  <label className="ccm-label">Client Name *</label>
                  <div className="ccm-suggestion-wrapper">
                    <input type="text" className="ccm-input" placeholder="Type client name..."
                      value={clientName} onChange={(e) => handleClientNameChange(e.target.value)}
                      onFocus={() => clientName && setShowSuggestions(true)} autoComplete="off" />
                    {showSuggestions && clientSuggestions.length > 0 && (
                      <div className="ccm-suggestions">
                        {clientSuggestions.map((c, i) => (
                          <div key={i} className="ccm-suggestion-item" onClick={() => handleSelectClient(c)}>
                            <div className="ccm-suggestion-name">
                              {c.clientName}
                              {c.storageStatus === 'legacy' && <span className="ccm-backup-badge">Backup</span>}
                            </div>
                            {c.clientAddress && <div className="ccm-suggestion-address">{c.clientAddress}</div>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="ccm-form-group">
                  <label className="ccm-label">Address (Optional)</label>
                  <div className="ccm-suggestion-wrapper">
                    <input type="text" className="ccm-input" placeholder="Type or select address..."
                      value={clientAddress} onChange={(e) => handleClientAddressChange(e.target.value)}
                      onFocus={() => clientAddress && setShowAddressSuggestions(true)} autoComplete="off" />
                    {showAddressSuggestions && addressSuggestions.length > 0 && (
                      <div className="ccm-suggestions">
                        {addressSuggestions.map((c, i) => (
                          <div key={i} className="ccm-suggestion-item" onClick={() => handleSelectAddress(c)}>
                            <div className="ccm-suggestion-name">
                              {c.clientAddress}
                              {c.storageStatus === 'legacy' && <span className="ccm-backup-badge">Backup</span>}
                            </div>
                            <div className="ccm-suggestion-address">{c.clientName}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="ccm-form-group">
                  <label className="ccm-label">Inspection Date *</label>
                  <input type="date" className="ccm-input" value={inspectionDate}
                    onChange={(e) => setInspectionDate(e.target.value)} max={today} required />
                </div>
                <div className="ccm-button-group">
                  <button className="ccm-btn ccm-btn--primary" onClick={handleStartCamera} disabled={!clientName}>
                    <AiOutlineCamera size={16} />Open Camera
                  </button>
                  <button className="ccm-btn ccm-btn--secondary"
                    onClick={() => { setMode('capture'); fileInputRef.current?.click(); }} disabled={!clientName}>
                    {videoEnabled ? 'Upload Photos / Videos' : 'Upload Photos'}
                  </button>
                </div>
              </div>
            )}

            {/* ── Capture screen ── */}
            {mode === 'capture' && (
              <div className="ccm-section">
                <div className="ccm-capture-banner">
                  <strong>{clientName}</strong>
                  <span>{new Date(inspectionDate + 'T12:00:00').toLocaleDateString()}</span>
                </div>

                {cameraStream ? (
                  <>
                    <div
                      className={`ccm-video-wrap${zoomDragging ? ' ccm-video-wrap--zooming' : ''}`}
                      onPointerDown={handleZoomPointerDown}
                      onPointerMove={handleZoomPointerMove}
                      onPointerUp={handleZoomPointerUp}
                      onPointerCancel={handleZoomPointerUp}
                    >
                      <video ref={videoRef} autoPlay playsInline className="ccm-video"
                        style={!zoomCapabilities && zoomLevel > 1 ? { transform: `scale(${zoomLevel})` } : undefined} />

                      {/* Live readout while a gesture is running, plus a one-off hint
                          at 1x so the gesture is discoverable without a manual. */}
                      {zoomDragging ? (
                        <div className="ccm-zoom-readout">{zoomLevel.toFixed(1)}x</div>
                      ) : (
                        zoomLevel <= 1.01 && <div className="ccm-zoom-hint">Pinch with two fingers to zoom</div>
                      )}
                      {recording && (
                        <div className="ccm-rec-indicator" aria-live="polite">
                          <span className="ccm-rec-dot" /> {formatDuration(recordSeconds * 1000)}
                        </div>
                      )}
                      {torchSupported && (
                        <button type="button"
                          className={`ccm-torch-btn${torchOn ? ' ccm-torch-btn--on' : ''}`}
                          onClick={handleToggleTorch}
                          aria-label={torchOn ? 'Turn flash off' : 'Turn flash on'}>
                          {torchOn ? <AiFillThunderbolt size={18} /> : <AiOutlineThunderbolt size={18} />}
                        </button>
                      )}
                    </div>
                    <canvas ref={canvasRef} style={{ display: 'none' }} />
                    {canRecordVideo && (
                      <div className="ccm-kind-toggle" role="tablist" aria-label="Capture mode">
                        <button type="button" role="tab" aria-selected={captureKind === 'photo'}
                          className={`ccm-kind-btn${captureKind === 'photo' ? ' ccm-kind-btn--active' : ''}`}
                          onClick={() => handleSelectKind('photo')} disabled={recording}>
                          <AiOutlineCamera size={15} /> Photo
                        </button>
                        <button type="button" role="tab" aria-selected={captureKind === 'video'}
                          className={`ccm-kind-btn${captureKind === 'video' ? ' ccm-kind-btn--active' : ''}`}
                          onClick={() => handleSelectKind('video')} disabled={recording}>
                          <AiOutlineVideoCamera size={15} /> Video
                        </button>
                      </div>
                    )}
                    {captureKind === 'video' && noMicNotice && (
                      <p className="ccm-mic-notice">Microphone is off — videos will record without sound.</p>
                    )}
                    <div className="ccm-button-group">
                      {captureKind === 'video' ? (
                        <button
                          className={`ccm-btn ccm-btn--record${recording ? ' ccm-btn--recording' : ''}`}
                          onClick={recording ? handleStopRecording : handleStartRecording}
                        >
                          <span className={recording ? 'ccm-stop-icon' : 'ccm-record-icon'} aria-hidden="true" />
                          {recording ? 'Stop' : 'Record'}
                        </button>
                      ) : (
                        <button className="ccm-btn ccm-btn--primary" onClick={handleCapturePhoto}>
                          <AiOutlineCamera size={16} />Capture
                        </button>
                      )}
                      <button className="ccm-btn ccm-btn--secondary" onClick={() => fileInputRef.current?.click()}
                        disabled={recording}>
                        From Library
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="ccm-button-group">
                    <button className="ccm-btn ccm-btn--primary" onClick={handleStartCamera}>
                      <AiOutlineCamera size={16} />Open Camera
                    </button>
                    <button className="ccm-btn ccm-btn--secondary" onClick={() => fileInputRef.current?.click()}>
                      Choose from Library
                    </button>
                  </div>
                )}

                {/* Photo filmstrip — all captured/picked photos */}
                {capturedPhotos.length > 0 && (
                  <div className="ccm-photo-strip">
                    {capturedPhotos.map((photo) => (
                      <div key={photo.id}
                        className={`ccm-photo-card ccm-photo-card--${photo.status}`}>
                        {photo.preview ? (
                          <img src={photo.preview} alt="" />
                        ) : (
                          <div className="ccm-video-placeholder"><AiOutlineVideoCamera size={22} /></div>
                        )}
                        {photo.kind === 'video' && (
                          <span className="ccm-video-badge">
                            {photo.status === 'uploading' && photo.progress < 1
                              ? `${Math.round((photo.progress || 0) * 100)}%`
                              : `▶ ${formatDuration(photo.durationMs) || ''}`}
                          </span>
                        )}
                        {/* Status badge */}
                        {photo.status === 'done' && (
                          <span className="ccm-photo-status ccm-photo-status--done">
                            <AiOutlineCheckCircle size={13} />
                          </span>
                        )}
                        {photo.status === 'failed' && (
                          <span className="ccm-photo-status ccm-photo-status--failed">
                            <AiOutlineCloseCircle size={13} />
                          </span>
                        )}
                        {(photo.status === 'uploading' || photo.status === 'pending') && (
                          <span className="ccm-photo-status ccm-photo-status--uploading">
                            <span className="ccm-spinner ccm-spinner--sm" />
                          </span>
                        )}
                        {/* A failed photo can be discarded instead of retried */}
                        {photo.status === 'failed' && (
                          <button className="ccm-remove-btn" onClick={() => handleRemovePhoto(photo.id)} title="Discard">
                            <AiOutlineDelete size={16} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Photos upload as they're taken; this just reports progress and closes. */}
                {capturedPhotos.length > 0 && (
                  <p className="ccm-upload-status" aria-live="polite">
                    {uploading
                      ? `Uploading ${noun(inFlight)}… you can carry on`
                      : `${noun(counts.done)} uploaded`}
                    {counts.failed > 0 && ` · ${counts.failed} failed`}
                  </p>
                )}
                {counts.failed > 0 && (
                  <button className="ccm-btn ccm-btn--secondary ccm-btn--upload" onClick={handleRetryFailed}>
                    <AiOutlineReload size={15} />
                    Retry {counts.failed} failed
                  </button>
                )}
                <button
                  className="ccm-btn ccm-btn--primary ccm-btn--upload"
                  onClick={handleClose}

                >
                  {uploading ? `Done — ${inFlight} still uploading` : 'Done'}
                </button>
              </div>
            )}

            <input ref={fileInputRef} type="file" multiple accept={videoEnabled ? 'image/*,video/*' : 'image/*'}
              onChange={handleFileSelect} style={{ display: 'none' }} />
          </div>
        </div>
      </div>

    </>
  );
};

export default CameraCaptureModal;
